import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MessageSquare, MoreHorizontal, Loader2, Plus } from "lucide-react";
import { http, apiError, formatDateTime } from "../../lib/api";
import { toLocalInput } from "./inquiryUi";

export const METHODS = { phone: ["Phone", Phone], email: ["Email", Mail], text: ["Text Message", MessageSquare], other: ["Other", MoreHorizontal] };

const CommForm = ({ item, onSaved, onCancel }) => {
  const [f, setF] = useState({ customer_name: item.name || "", at: toLocalInput(new Date().toISOString()), method: "phone", summary: "", concerns: "", next_steps: "", follow_up_at: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, at: new Date(f.at).toISOString(), follow_up_at: f.follow_up_at ? new Date(f.follow_up_at).toISOString() : null };
      const { data } = await http.post(`/admin/inquiries/${item.id}/communications`, body);
      toast.success(f.follow_up_at ? "Communication logged and follow-up scheduled" : "Communication logged");
      onSaved(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="border border-[rgba(212,175,55,0.35)] p-4 space-y-3 bg-white/[0.02]" data-testid="comm-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="label">Customer name</label><input className="field !py-2.5" value={f.customer_name} onChange={set("customer_name")} data-testid="comm-customer-input" /></div>
        <div><label className="label">Date & time *</label><input required type="datetime-local" className="field !py-2.5" value={f.at} onChange={set("at")} data-testid="comm-at-input" /></div>
      </div>
      <div>
        <label className="label">Method *</label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(METHODS).map(([k, [label, Icon]]) => (
            <button type="button" key={k} onClick={() => setF({ ...f, method: k })} className={`inline-flex items-center gap-2 px-3 py-2 text-xs border transition-colors ${f.method === k ? "bg-[var(--gold)] text-[#0a0d14] border-[var(--gold)]" : "border-white/15 text-slate-200 hover:border-[var(--gold)]"}`} data-testid={`comm-method-${k}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>
      <div><label className="label">Summary *</label><textarea required rows={3} className="field" value={f.summary} onChange={set("summary")} data-testid="comm-summary-input" /></div>
      <div><label className="label">Customer questions or concerns</label><textarea rows={2} className="field" value={f.concerns} onChange={set("concerns")} data-testid="comm-concerns-input" /></div>
      <div><label className="label">Agreed next steps</label><textarea rows={2} className="field" value={f.next_steps} onChange={set("next_steps")} data-testid="comm-next-input" /></div>
      <div><label className="label">Follow-up date & time (optional, updates the inquiry follow-up)</label><input type="datetime-local" className="field !py-2.5 !w-auto" value={f.follow_up_at} onChange={set("follow_up_at")} data-testid="comm-followup-input" /></div>
      <div className="flex gap-2">
        <button className="btn-gold !py-2.5" disabled={busy} data-testid="comm-save">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save entry</button>
        <button type="button" className="btn-ghost !py-2.5" onClick={onCancel}>Cancel</button>
      </div>
      <p className="text-xs text-slate-400">Entries are permanent and cannot be edited or deleted, so the history stays complete.</p>
    </form>
  );
};

const Entry = ({ c, latest }) => {
  const [label, Icon] = METHODS[c.method] || METHODS.other;
  return (
    <li className={`relative pl-8 pb-6 ${latest ? "" : "opacity-95"}`} data-testid={`comm-entry-${c.id}`}>
      <span className={`absolute left-0 top-0 w-6 h-6 grid place-items-center border ${latest ? "bg-[var(--gold)] text-[#0a0d14] border-[var(--gold)]" : "border-white/20 text-slate-300 bg-[var(--ink)]"}`}><Icon className="w-3.5 h-3.5" /></span>
      <div className={`border p-4 ${latest ? "border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.06)]" : "border-white/10 bg-white/[0.02]"}`}>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {latest && <span className="font-mono uppercase tracking-wide bg-[var(--gold)] text-[#0a0d14] px-1.5 py-0.5" data-testid="comm-latest-badge">Latest</span>}
          <span className="text-slate-100 font-medium">{label}</span>
          <span className="text-slate-400">· {formatDateTime(c.at)} · with {c.customer_name}</span>
        </div>
        <p className="text-sm text-slate-100 mt-2 whitespace-pre-line">{c.summary}</p>
        {c.concerns && <p className="text-sm text-slate-300 mt-2 whitespace-pre-line"><span className="text-slate-400">Questions / concerns: </span>{c.concerns}</p>}
        {c.next_steps && <p className="text-sm text-slate-300 mt-2 whitespace-pre-line"><span className="text-slate-400">Next steps: </span>{c.next_steps}</p>}
        {c.follow_up_at && <p className="text-xs text-amber-200 mt-2">Follow-up set for {formatDateTime(c.follow_up_at)}</p>}
        <p className="text-xs text-slate-400 mt-3">Recorded by {c.recorded_by} (<span className="capitalize">{c.recorded_role}</span>) · {formatDateTime(c.recorded_at)}</p>
      </div>
    </li>
  );
};

export const CommLog = ({ item, onChange }) => {
  const [adding, setAdding] = useState(false);
  const list = [...(item.communications || [])].sort((a, b) => (a.at < b.at ? 1 : -1));
  return (
    <section data-testid="comm-log">
      <div className="flex items-center justify-between mb-3">
        <p className="eyebrow">Customer communication <span className="text-slate-400 normal-case tracking-normal">({list.length}, newest first)</span></p>
        {!adding && <button className="btn-ghost !py-2 !px-3 !text-xs" onClick={() => setAdding(true)} data-testid="comm-add-button"><Plus className="w-3 h-3" /> Log communication</button>}
      </div>
      {adding && <div className="mb-5"><CommForm item={item} onCancel={() => setAdding(false)} onSaved={(d) => { setAdding(false); onChange(d); }} /></div>}
      {list.length ? (
        <ol className="relative before:absolute before:left-3 before:top-2 before:bottom-6 before:w-px before:bg-white/10">
          {list.map((c, i) => <Entry key={c.id} c={c} latest={i === 0} />)}
        </ol>
      ) : <p className="text-sm text-slate-400">No calls, emails or messages logged yet.</p>}
    </section>
  );
};
